import React from 'react';
import * as Yup from 'yup';
import { Row, Col } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { Formik, FormikProps } from 'formik';
import { Form } from 'formik-antd';
import { isNil } from 'lodash';

import EntityCard from './EntityCard';
import Radio from 'components/formik/Radio';
import FormItem from 'components/formik/FormItem';
import FormikEffect from 'components/formik/FormikEffect';
import { BOUserTypeEnum, ProfileTypeEnum } from 'api/backofficeApiClient';

import styles from './EntitySelection.module.less';

export interface Profile {
  entity: BOUserTypeEnum | null;
  type: ProfileTypeEnum | null;
}

type Props = {
  formRef: any;
  onReset: () => void;
  profile: Profile;
  availableSelectionRoles: ProfileTypeEnum[];
  onProfileChange: React.Dispatch<React.SetStateAction<Profile>>;
  onChange: (formik: FormikProps<EntitySelectionFormValues>) => void;
};

export interface EntitySelectionFormValues {
  entity: BOUserTypeEnum | null;
  profileType: ProfileTypeEnum | null;
}

export const isProfileTypeRequired = (entity: BOUserTypeEnum | null) =>
  !isNil(entity) && entity !== BOUserTypeEnum.SelfEmployed;

const getProfileTypeDescriptionTitle = (
  profileType: ProfileTypeEnum | null,
) => {
  switch (profileType) {
    case ProfileTypeEnum.Recruiter:
      return 'Staffing company';

    case ProfileTypeEnum.Corporate:
      return 'Corporate talent acquisition';

    default:
      return '';
  }
};

const getEntityDescription = (values: EntitySelectionFormValues) => {
  const { entity, profileType } = values;
  const profileTypeDesciptionTitle =
    getProfileTypeDescriptionTitle(profileType);

  switch (entity) {
    case BOUserTypeEnum.Company:
      return (
        <>
          <div className={styles.entityDescriptionTitle}>
            Company. {profileTypeDesciptionTitle}
          </div>
          <div>
            An operating business entity based in the United States. Please
            note: This type of member setup includes Corporate talent
            acquisition and Staffing agencies.
          </div>
        </>
      );

    case BOUserTypeEnum.SelfEmployed:
      return (
        <>
          <div className={styles.entityDescriptionTitle}>Independent</div>
          <div>
            A U.S. based recruiter who works independently and has no additional
            employees. Please note: This type of member setup operates under
            their SSN or Single member EIN.
          </div>
        </>
      );

    case BOUserTypeEnum.International:
      return (
        <>
          <div className={styles.entityDescriptionTitle}>
            International. {profileTypeDesciptionTitle}
          </div>
          <div>
            An international company that is not registered as a legal company
            in the United States. Please note: This type of member setup will
            require the completion of a W-8 BEN-E.
          </div>
        </>
      );

    default:
      return <div>Text will be displayed here.</div>;
  }
};

const validationSchema = Yup.object({
  entity: Yup.number()
    .nullable()
    .required(
      'Please select an entity type in order to proceed with registration',
    ),
  profileType: Yup.number()
    .nullable()
    .when('entity', {
      is: isProfileTypeRequired,
      then: Yup.number()
        .nullable()
        .required(
          'Please select the profile type in order to proceed with registration',
        ),
    }),
});

export default React.memo((props: Props) => {
  const {
    formRef,
    onChange,
    profile,
    onReset,
    onProfileChange,
    availableSelectionRoles,
  } = props;
  const initialValues: EntitySelectionFormValues = {
    entity: profile.entity,
    profileType: profile.type,
  };

  const handleEntityChange = (
    event: RadioChangeEvent,
    formik: FormikProps<EntitySelectionFormValues>,
  ) => {
    const { value } = event.target;

    formik.setFieldTouched('profileType', false, false);
    formik.setFieldValue('profileType', null, false);

    onReset();
    onProfileChange({
      entity: value,
      type: null,
    });

    setTimeout(() => {
      // Reset errors on entity change
      formik.setErrors({});
    }, 0);
  };

  const handleProfileTypeChange = (event: RadioChangeEvent) => {
    const { value } = event.target;

    onReset();
    onProfileChange((profile) => ({
      ...profile,
      type: value as ProfileTypeEnum,
    }));
  };

  return (
    <Formik
      innerRef={formRef}
      onSubmit={() => {}}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form className={styles.wrapper}>
            <div className="modal-scroll-wrapper">
              <div className={styles.entityTitle}>
                Please select an entity in order to proceed with initial profile
                setup.
              </div>

              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <Row>
                    <Col xs={24}>
                      <FormItem name="entity">
                        <Radio.Group
                          className={styles.radioGroup}
                          name="entity"
                          onChange={(e) => handleEntityChange(e, formik)}
                        >
                          <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
                            <Col xs={8}>
                              <Radio.Button
                                className="radio-card-button"
                                value={BOUserTypeEnum.Company}
                              >
                                <EntityCard
                                  title="U.S. company"
                                  icon={
                                    <div className="holder">
                                      <span className="icon-wrapper">
                                        <span className="icon icon-empire-state" />
                                      </span>
                                    </div>
                                  }
                                />
                              </Radio.Button>
                            </Col>

                            <Col xs={8}>
                              <Radio.Button
                                className="radio-card-button"
                                value={BOUserTypeEnum.SelfEmployed}
                              >
                                <EntityCard
                                  title="Independent recruiter"
                                  icon={
                                    <div className="holder">
                                      <span className="icon-wrapper">
                                        <span className="icon icon-paper" />
                                      </span>
                                    </div>
                                  }
                                />
                              </Radio.Button>
                            </Col>

                            <Col xs={8}>
                              <Radio.Button
                                className="radio-card-button"
                                value={BOUserTypeEnum.International}
                              >
                                <EntityCard
                                  title="International company"
                                  icon={
                                    <div className="holder">
                                      <span className="icon-wrapper">
                                        <span className="icon icon-writing-tool-user" />
                                      </span>
                                    </div>
                                  }
                                />
                              </Radio.Button>
                            </Col>
                          </Row>
                        </Radio.Group>
                      </FormItem>
                    </Col>
                  </Row>

                  {isProfileTypeRequired(formik.values.entity) && (
                    <Row>
                      <Col xs={24}>
                        <div className={styles.subTitle}>
                          <span>Company</span>
                        </div>

                        <FormItem name="profileType">
                          <Radio.Group
                            className={styles.radioGroup}
                            name="profileType"
                            onChange={handleProfileTypeChange}
                          >
                            <Row
                              gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}
                              justify="end"
                            >
                              <Col xs={8} />

                              {availableSelectionRoles.includes(
                                ProfileTypeEnum.Recruiter,
                              ) && (
                                <Col xs={8}>
                                  <Radio.Button
                                    className="radio-card-button"
                                    value={ProfileTypeEnum.Recruiter}
                                  >
                                    <EntityCard
                                      title="Staffing company"
                                      icon={
                                        <div className="holder">
                                          <span className="icon-wrapper">
                                            <span className="icon icon-magnifier" />
                                          </span>
                                        </div>
                                      }
                                    />
                                  </Radio.Button>
                                </Col>
                              )}

                              {availableSelectionRoles.includes(
                                ProfileTypeEnum.Corporate,
                              ) && (
                                <Col xs={8}>
                                  <Radio.Button
                                    className="radio-card-button"
                                    value={ProfileTypeEnum.Corporate}
                                  >
                                    <EntityCard
                                      title="Corporate talent acquisition"
                                      icon={
                                        <div className="holder">
                                          <span className="icon-wrapper">
                                            <span className="icon icon-location" />
                                          </span>
                                        </div>
                                      }
                                    />
                                  </Radio.Button>
                                </Col>
                              )}
                            </Row>
                          </Radio.Group>
                        </FormItem>
                      </Col>
                    </Row>
                  )}
                </Col>

                <Col xs={24} md={12}>
                  <div className={styles.entityDescription}>
                    {getEntityDescription(formik.values)}
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
